/**
 * workspaceRoster — display mapping for the server-resolved member roster
 * (owner directive 2026-08-17: the organization page's members section must
 * be accurate and consistent with Team & Roles at all times).
 *
 * Input is rbacListMembers rows — the same authority Team & Roles renders:
 * profile-joined displayName, three-source isOwner rule, primaryRoleId.
 * Pure leaf module (no imports) so every surface shapes rows identically.
 */

export interface RosterRow {
  uid: string;
  name: string;
  roleLabel: string;
  isOwner: boolean;
  paused: boolean;
}

/** Map server roster rows to display rows: owner-first, then by name. */
export function toRosterRows(members: unknown): RosterRow[] {
  if (!Array.isArray(members)) return [];
  const rows = members
    .map((raw) => {
      const m = (raw || {}) as Record<string, unknown>;
      const uid = String(m.userId || m.id || '').trim();
      const isOwner = m.isOwner === true;
      const status = String(m.status ?? 'active').toLowerCase();
      return {
        uid,
        name: typeof m.displayName === 'string' ? m.displayName.trim() : '',
        roleLabel: isOwner ? 'owner' : String(m.primaryRoleId || 'member').trim() || 'member',
        isOwner,
        paused: status === 'paused' || status === 'suspended',
      };
    })
    .filter((r) => r.uid);
  return rows.sort((a, b) => {
    if (a.isOwner !== b.isOwner) return a.isOwner ? -1 : 1;
    if (!a.name !== !b.name) return a.name ? -1 : 1; // unnamed rows last
    return a.name.localeCompare(b.name);
  });
}
